"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { b2cRegister, b2cVerifyOTP, b2cResendOTP } from "../api/b2cUserApi"
import Swal from "sweetalert2"
import { useOutletContext } from "react-router-dom"

const UserSignup = () => {
  const navigate = useNavigate()
  const { companyName } = useParams()
  const { isB2CAuth } = useOutletContext()

  // Form state
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  })
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isB2CAuth) {
      navigate(`/${companyName}`)
    }
  }, [isB2CAuth, navigate, companyName])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSignup = async (e) => {
    e.preventDefault()

    // Validation
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Passwords do not match",
      })
      return
    }

    if (formData.password.length < 6) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Password must be at least 6 characters long",
      })
      return
    }

    setLoading(true)

    try {
      const response = await b2cRegister({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        companyName: companyName,
      })

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Registration Successful",
          text: response.message,
        })
        setStep(2)
      } else {
        Swal.fire({
          icon: "error",
          title: "Registration Failed",
          text: response.message || "Signup failed",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An error occurred during signup",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOTP = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await b2cVerifyOTP(formData.email, otp)

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "Email Verified",
          text: response.message,
          timer: 2000,
          showConfirmButton: false,
        })
        setTimeout(() => {
          navigate(`/${companyName}/login`)
        }, 2000)
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: response.message || "OTP verification failed",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An error occurred during verification",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResendOTP = async () => {
    setLoading(true)

    try {
      const response = await b2cResendOTP(formData.email)

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "OTP Resent",
          text: response.message || "OTP resent successfully! Please check your email.",
        })
      } else {
        const isAlreadyVerified =
          (response.message && response.message.includes("already verified")) ||
          (response.error && response.error.message && response.error.message.includes("already verified"))

        if (isAlreadyVerified) {
          Swal.fire({
            icon: "info",
            title: "Email Already Verified",
            text: "Your email is already verified. Please proceed to login.",
            confirmButtonText: "Go to Login",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/${companyName}/login`)
            }
          })
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed to Resend",
            text: response.message || "Failed to resend OTP. Please try again.",
          })
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "An error occurred while resending OTP",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* SIGNUP SECTION */}
      <section className="features py-5">
        <div className="container-lg">
          <div className="row g-4 justify-content-center">
            {/* Signup Form */}
            <div className="col-lg-6">
              <div className="info-card">
                {step === 1 ? (
                  <>
                    <h4 className="fw-bold mb-3">Create Your Account</h4>

                    <form onSubmit={handleSignup}>
                      <div className="mb-3">
                        <label className="form-label">Full Name</label>
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Enter your phone number"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Password</label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Confirm Password</label>
                        <input
                          type="password"
                          name="confirmPassword"
                          className="form-control"
                          placeholder="Confirm your password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <button type="submit" className="btn btn-primary-navy w-100" disabled={loading}>
                        {loading ? "Creating Account..." : "Create Account"}
                      </button>

                      <div className="text-center mt-3">
                        <p className="mb-0">
                          Already have an account?{" "}
                          <Link
                            to={`/${companyName}/login`}
                            className="fw-bold"
                            style={{ color: "#002B5C", textDecoration: "none" }}
                          >
                            Login
                          </Link>
                        </p>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    <h4 className="fw-bold mb-3">Verify Your Email</h4>
                    <p className="text-muted mb-4">
                      We've sent a verification code to <strong>{formData.email}</strong>. Please enter it below.
                    </p>

                    <form onSubmit={handleVerifyOTP}>
                      <div className="mb-3">
                        <label className="form-label">Enter OTP</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter 6-digit code"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          maxLength="6"
                          required
                        />
                      </div>

                      <button type="submit" className="btn btn-primary-navy w-100 mb-2" disabled={loading}>
                        {loading ? "Verifying..." : "Verify Account"}
                      </button>

                      <button
                        type="button"
                        className="btn btn-outline-navy w-100"
                        onClick={handleResendOTP}
                        disabled={loading}
                      >
                        Resend OTP
                      </button>

                      <div className="text-center mt-3">
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => setStep(1)}
                          style={{ color: "#002B5C", textDecoration: "none" }}
                        >
                          ← Back to Signup
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default UserSignup
